import React, { useState, useEffect, useRef } from 'react';
import './Search.css';
import backIcon from './assets/backspace-icon.png';
import closeIcon from './assets/close-icon.png';
import { useNavigate } from 'react-router-dom';
import api from './api';

import NewsTab from './components/news/NewsTab';
import placeholderProfile from './assets/placeholder-profile.png';
import placeholderLogo from './assets/placeholder-logo.png';
import storeIcon from './assets/mystore-icon.png';
import userIcon from './assets/User-icon.png';
import deleteIcon from './assets/delete-icon.png';

import { useTheme } from './context/ThemeContext';
import { usePiAppsDiscovery } from './context/PiAppsDiscoveryContext';
import deleteIconDark from './assets/deletedark-icon.png';
import goldTickIcon from './assets/goldtick-icon.png';

import BottomNav from './components/navigation/BottomNav';
import { useMessageUnreadContext } from './context/MessageUnreadContext';

const RECENT_USERS_KEY = 'bf_recent_users_v1';
const MAX_RECENT_USERS = 15;

const SEARCH_TABS = [
  { key: 'forYou', label: 'For You' },
  { key: 'news', label: 'News' },
  { key: 'piApps', label: 'Pi Apps' },
  { key: 'merchants', label: 'Merchants' },
  { key: 'stores', label: 'Stores' },
  { key: 'official', label: 'Official' },
];


function Search({ user }) {
  const [query, setQuery] = useState('');
  const [activeTab, setActiveTab] = useState('forYou');

const { unreadDmCount } = useMessageUnreadContext();

const [bottomNavHidden, setBottomNavHidden] = useState(false);

const scrollAreaRef = useRef(null);
const scrollAnchorRef = useRef(0);

const handleSearchScroll = (event) => {
  const element = event.currentTarget;

  // Clamp the value to avoid iOS overscroll affecting direction.
  const maxScrollTop = Math.max(
    0,
    element.scrollHeight - element.clientHeight
  );

  const scrollTop = Math.min(
    maxScrollTop,
    Math.max(0, element.scrollTop)
  );

  const delta = scrollTop - scrollAnchorRef.current;

  if (scrollTop <= 80) {
    setBottomNavHidden(false);
    scrollAnchorRef.current = scrollTop;
    return;
  }

  // Small movements accumulate until they reach the threshold.
  if (Math.abs(delta) >= 10) {
    setBottomNavHidden(delta > 0);
    scrollAnchorRef.current = scrollTop;
  }
};

// Show the navigation again when changing Search tabs.
useEffect(() => {
  setBottomNavHidden(false);

  scrollAnchorRef.current =
    scrollAreaRef.current?.scrollTop || 0;
}, [activeTab]);

  // Existing For You search results
  const [userResults, setUserResults] = useState([]);
  const [storeResults, setStoreResults] = useState([]);

  // New discovery tab results
  const [discoveryResults, setDiscoveryResults] = useState([]);
  const [discoveryLoading, setDiscoveryLoading] = useState(false);

  const [recentUsers, setRecentUsers] = useState([]);
  const [recentNewCounts, setRecentNewCounts] = useState({});

  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const navigate = useNavigate();

  const {
    topApps,
    topAppsLoading,
    topAppsLoaded,
    refreshTopApps,
    updateTopAppFollowState,
  } = usePiAppsDiscovery();

  useEffect(() => {
    refreshTopApps();
  }, [refreshTopApps]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(RECENT_USERS_KEY);

      if (raw) {
        const parsed = JSON.parse(raw);

        if (Array.isArray(parsed)) {
          setRecentUsers(parsed);
        }
      }
    } catch (err) {
      console.error('Failed to load recent users:', err);
    }
  }, []);

  const saveRecentUsers = (list) => {
    try {
      localStorage.setItem(RECENT_USERS_KEY, JSON.stringify(list));
    } catch (err) {
      console.error('Failed to save recent users:', err);
    }
  };

  const addRecentUser = (usr) => {
    if (!usr || !usr.username) return;

    try {
      const raw = localStorage.getItem(RECENT_USERS_KEY);

      let existing = [];
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          existing = parsed;
        }
      }

      const filtered = existing.filter((u) => u.username !== usr.username);

      const updated = [
        {
          username: usr.username,
          name: usr.name || '',
          profilePic: usr.profilePic || '',
          adminRole: usr.adminRole || null,
          isAdmin: usr.isAdmin || false,
          accountType: usr.accountType || 'primary',
          isApp: usr.isApp || usr.accountType === 'app' || false,
        },
        ...filtered,
      ].slice(0, MAX_RECENT_USERS);

      localStorage.setItem(RECENT_USERS_KEY, JSON.stringify(updated));
      setRecentUsers(updated);
    } catch (err) {
      console.error('Failed to update recent users:', err);
    }
  };

  const handleRemoveRecent = (username, e) => {
    e.stopPropagation();

    setRecentUsers((prev) => {
      const updated = prev.filter((u) => u.username !== username);
      saveRecentUsers(updated);
      return updated;
    });
  };

  const handleUserResultClick = (usr) => {
    addRecentUser(usr);
    navigate(`/user/${usr.username}`);
  };

  const handleAppClick = (app) => {
    if (!app?.username) return;

    addRecentUser({
      username: app.username,
      name: app.name,
      profilePic: app.profilePic,
      accountType: 'app',
      isApp: true,
    });

    navigate(`/user/${app.username}`);
  };

  const handleDiscoveryClick = (item) => {
    if (!item) return;

    if (item.type === 'store') {
      const tag = item.businessUsername || item.slug || item.username;
      if (tag) navigate(`/store/${tag}`);
      return;
    }

    if (item.username) {
      addRecentUser({
        username: item.username,
        name: item.name,
        profilePic: item.profilePic,
        adminRole: item.adminRole || null,
        isAdmin: item.isAdmin || item.type === 'official',
        accountType: item.accountType || (item.type === 'piApp' ? 'app' : 'primary'),
        isApp: item.isApp || item.type === 'piApp',
      });

      navigate(`/user/${item.username}`);
    }
  };

  // Existing For You search behavior
  useEffect(() => {
    const searchAll = async () => {
  
    if (activeTab !== 'forYou') {
        setUserResults([]);
        setStoreResults([]);
        return;
      }

      const trimmed = query.trim();

      if (trimmed === '') {
        setUserResults([]);
        setStoreResults([]);
        return;
      }

      if (trimmed.length < 2) {
        setUserResults([]);
        setStoreResults([]);
        return;
      }

      try {
        const [usersRes, storesRes] = await Promise.all([
          api.get(`/users/search?q=${encodeURIComponent(trimmed)}`),
          api.get(`/stores/search?q=${encodeURIComponent(trimmed)}`),
        ]);

        setUserResults(Array.isArray(usersRes.data) ? usersRes.data : []);
        setStoreResults(Array.isArray(storesRes.data) ? storesRes.data : []);
      } catch (err) {
        console.error('❌ Search failed:', err);
      }
    };

    searchAll();
  }, [query, activeTab]);

  const canShowFollowButton = (item) => {
    return (
      item?.type === 'piApp' ||
      item?.type === 'merchant' ||
      item?.type === 'official'
    );
  };

  const updateDiscoveryFollowState = (username, nowFollowing) => {
    const targetUsername = String(username || '').trim().toLowerCase();

    if (!targetUsername) return;

    setDiscoveryResults((prev) =>
      prev.map((item) => {
        const itemUsername = String(item?.username || '').trim().toLowerCase();

        if (!canShowFollowButton(item) || itemUsername !== targetUsername) {
          return item;
        }

        const currentCount = Number(item.followersCount || 0);
        const wasFollowing = Boolean(item.isFollowing);

        let nextCount = currentCount;

        if (nowFollowing && !wasFollowing) {
          nextCount = currentCount + 1;
        }

        if (!nowFollowing && wasFollowing) {
          nextCount = Math.max(0, currentCount - 1);
        }

        return {
          ...item,
          isFollowing: Boolean(nowFollowing),
          followersCount: nextCount,
        };
      })
    );
  };

  const handleToggleSearchFollow = async (e, item) => {
    e.stopPropagation();

    if (!user?.username || !item?.username) return;
    if (!canShowFollowButton(item)) return;

    const follower = String(user.username).trim().toLowerCase();
    const following = String(item.username).trim().toLowerCase();

    if (!follower || !following || follower === following) return;

    const wasFollowing = Boolean(item.isFollowing);
    const optimisticNext = !wasFollowing;

    // Top Pi Apps live in context, so update that list too.
    if (item.type === 'piApp') {
      updateTopAppFollowState(following, optimisticNext);
    }

    updateDiscoveryFollowState(following, optimisticNext);

    try {

const res = await api.post('/search/toggle-follow', {
  username: item.username,
});

      const nowFollowing =
        typeof res.data?.nowFollowing === 'boolean'
          ? res.data.nowFollowing
          : optimisticNext;

      if (item.type === 'piApp') {
        updateTopAppFollowState(following, nowFollowing);
      }

      updateDiscoveryFollowState(following, nowFollowing);
    } catch (err) {
      console.error('❌ Search follow toggle failed:', err);

      if (item.type === 'piApp') {
        updateTopAppFollowState(following, wasFollowing);
      }

      updateDiscoveryFollowState(following, wasFollowing);
    }
  };

  const renderSearchFollowButton = (item) => {
    if (!user?.username || !item?.username) return null;
    if (!canShowFollowButton(item)) return null;

    const viewerUsername = String(user.username).trim().toLowerCase();
    const targetUsername = String(item.username).trim().toLowerCase();

    if (!viewerUsername || viewerUsername === targetUsername) {
      return null;
    }

    const following = Boolean(item.isFollowing);

    return (
      <button
        type="button"
        className={`search-pi-app-follow-btn ${
          following ? 'search-pi-app-follow-btn--following' : ''
        }`}
        onClick={(e) => handleToggleSearchFollow(e, item)}
      >
        {following ? 'Following' : 'Follow'}
      </button>
    );
  };

  // New discovery tabs behavior
  useEffect(() => {
    const fetchDiscovery = async () => {

if (
  activeTab === 'forYou' ||
  activeTab === 'news'
) {
  setDiscoveryResults([]);
  setDiscoveryLoading(false);
  return;
}

      try {
        setDiscoveryLoading(true);

        const trimmed = query.trim();

        // Empty query is allowed for discovery tabs.
        // If user types only 1 character, we still show default list to avoid noisy requests.
        const safeQuery = trimmed.length >= 2 ? trimmed : '';

        const viewerParam = user?.username
          ? `&viewer=${encodeURIComponent(user.username)}`
          : '';

        const res = await api.get(
          `/search/discover?tab=${encodeURIComponent(activeTab)}&q=${encodeURIComponent(safeQuery)}${viewerParam}`
        );

        setDiscoveryResults(Array.isArray(res.data?.results) ? res.data.results : []);
      } catch (err) {
        console.error('❌ Discovery search failed:', err);
        setDiscoveryResults([]);
      } finally {
        setDiscoveryLoading(false);
      }
    };

    fetchDiscovery();
  }, [activeTab, query, user?.username]);

  const escapeRegExp = (value = '') => {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  const highlightMatch = (text, queryValue) => {
    if (!text) return '';
    if (!queryValue || queryValue.trim().length < 2) return text;

    const safeQuery = escapeRegExp(queryValue.trim());
    const regex = new RegExp(`(${safeQuery})`, 'gi');

    return String(text).replace(regex, (match) => `<mark>${match}</mark>`);
  };

  const getStoreLogo = (store) => {
    const logo = store.businessLogo;

    if (
      !logo ||
      logo.trim() === '' ||
      logo === 'undefined' ||
      logo === null ||
      logo.includes('/uploads/store-logos/placeholder.png')
    ) {
      return placeholderLogo;
    }

    return logo;
  };

  const getAppLogo = (app) => {
    const logo = app?.profilePic || app?.logo || app?.appLogo;

    if (
      !logo ||
      String(logo).trim() === '' ||
      logo === 'undefined' ||
      logo === null
    ) {
      return placeholderLogo;
    }

    return logo;
  };

  useEffect(() => {
    const fetchNewCounts = async () => {
      if (!recentUsers || recentUsers.length === 0) {
        setRecentNewCounts({});
        return;
      }

      const usernames = recentUsers.map((u) => u.username).filter(Boolean);

      if (usernames.length === 0) {
        setRecentNewCounts({});
        return;
      }

      try {
        const queryParam = encodeURIComponent(usernames.join(','));
        const res = await api.get(
          `/users/activity/recent-new-counts?usernames=${queryParam}`
        );

        setRecentNewCounts(res.data || {});
      } catch (err) {
        console.error('Failed to fetch recent new counts:', err);
      }
    };

    fetchNewCounts();
  }, [recentUsers]);

  const formatNewLabel = (username) => {
    const n = recentNewCounts[username] || 0;

    if (!n || n <= 0) return '';
    if (n === 1) return '1 new';
    if (n <= 9) return `${n} new`;

    return '+9 new';
  };

  const formatFollowers = (count = 0) => {
    const n = Number(count) || 0;

    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M followers`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K followers`;
    if (n === 1) return '1 follower';

    return `${n} followers`;
  };

  const trimmedQuery = query.trim();

  const isAdminUser = (usr) => usr?.isAdmin === true || !!usr?.adminRole;
  const isAppUser = (usr) => usr?.isApp === true || usr?.accountType === 'app';
  const isSpecialUser = (usr) => isAdminUser(usr) || isAppUser(usr);

  const getSpecialTitle = (usr) => {
    if (isAdminUser(usr)) return usr?.adminRole || 'Admin';
    if (isAppUser(usr)) return 'App account';
    return '';
  };


const getSearchPlaceholder = () => {
  if (activeTab === 'news') {
    return 'Search Pi Network news...';
  }

  if (activeTab === 'piApps') {
    return 'Search Pi Apps...';
  }

  if (activeTab === 'merchants') {
    return 'Search merchants...';
  }

  if (activeTab === 'stores') {
    return 'Search stores...';
  }

  if (activeTab === 'official') {
    return 'Search official accounts...';
  }

  return 'Search usernames, names, stores...';
};

  const getDiscoveryEmptyText = () => {
    if (discoveryLoading) return 'Loading...';
    if (activeTab === 'merchants') return 'No active merchants found.';
    if (activeTab === 'stores') return 'No active stores found.';
    if (activeTab === 'piApps') return 'No public Pi Apps found.';
    if (activeTab === 'official') return 'No official accounts found.';

    return 'No results found.';
  };

  const renderTabs = () => {
    return (
      <div className="search-tabs-wrap">
        <div className="search-tabs-scroll">
          {SEARCH_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`search-tab-btn ${
                activeTab === tab.key ? 'search-tab-btn--active' : ''
              }`}

onClick={() => {
  setActiveTab(tab.key);
  setQuery('');
}}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>
    );
  };

  const renderRecentUsers = () => {
    if (recentUsers.length === 0) {
      return null;
    }

    return (
      <>
        <div className="search-section-header">
          <span className="search-section-title">Recent</span>
          <button
            type="button"
            className="search-section-link"
            onClick={() => setRecentUsers((prev) => prev)}
          >
            See all
          </button>
        </div>

        {recentUsers.map((usr) => {
          const newLabel = formatNewLabel(usr.username);

          return (
            <div
              className="search-entry recent-entry"
              key={`recent-${usr.username}`}
              onClick={() => {
                addRecentUser(usr);
                navigate(`/user/${usr.username}`);
              }}
            >
              <div className="recent-content">
                <img
                  src={usr.profilePic || placeholderProfile}
                  alt="User"
                  className={`search-avatar ${
                    isSpecialUser(usr) ? 'search-avatar-square' : ''
                  }`}
                />

                <div className="search-info">
                  <div className="search-title-line">
                    <span className="search-name-with-badge">
                      <strong>{usr.name || usr.username}</strong>

                      {isSpecialUser(usr) && (
                        <img
                          src={goldTickIcon}
                          alt={isAdminUser(usr) ? 'Admin verified' : 'App verified'}
                          className="search-gold-tick"
                          title={getSpecialTitle(usr)}
                        />
                      )}
                    </span>

                    {!isSpecialUser(usr) && (
                      <img
                        src={userIcon}
                        alt="user"
                        className="search-type-icon"
                        title="User"
                      />
                    )}
                  </div>

                  {newLabel ? (
                    <span className="recent-new-label">{newLabel}</span>
                  ) : (
                    <span className="recent-new-label recent-new-label--none"></span>
                  )}
                </div>
              </div>

              <button
                type="button"
                className="recent-remove-btn"
                onClick={(e) => handleRemoveRecent(usr.username, e)}
              >
                <img
                  src={isDark ? deleteIconDark : deleteIcon}
                  alt="Remove"
                  className="recent-remove-icon"
                />
              </button>
            </div>
          );
        })}
      </>
    );
  };

  const renderTopApps = () => {
    return (
      <div className="search-pi-apps-section">
        <div className="search-section-header search-section-header--apps">
          <span className="search-section-title">Pi Apps</span>

          <button
            type="button"
            className="search-section-link"
            onClick={() => {
              refreshTopApps();
              navigate('/pi-apps');
            }}
          >
            Explore more
          </button>
        </div>

        {topAppsLoading && !topAppsLoaded ? (
          <p className="search-apps-empty-note">Loading Pi Apps...</p>
        ) : topApps.length === 0 ? (
          <p className="search-apps-empty-note">No public Pi Apps yet.</p>
        ) : (
          <>
            {topApps.map((app) => {
              const appWithType = {
                ...app,
                type: 'piApp',
              };

              return (
                <div
                  className="search-entry search-app-card"
                  key={`top-app-${app._id || app.username}`}
                  onClick={() => handleAppClick(app)}
                >
                  <img
                    src={getAppLogo(app)}
                    alt={app.name || 'Pi App'}
                    className="search-avatar search-avatar-square search-app-logo"
                  />

                  <div className="search-info">
                    <div className="search-title-line">
                      <span className="search-name-with-badge">
                        <strong>{app.name || app.username}</strong>

                        <img
                          src={goldTickIcon}
                          alt="App verified"
                          className="search-gold-tick"
                          title="App account"
                        />
                      </span>
                    </div>

                    <div className="search-app-meta-line">
                      {app.category && (
                        <span className="search-app-category">{app.category}</span>
                      )}

                      <span className="search-app-followers">
                        {formatFollowers(app.followersCount)}
                      </span>
                    </div>

                    {(app.bio || app.domain) && (
                      <span className="search-app-snippet">
                        {app.bio || app.domain}
                      </span>
                    )}
                  </div>

                  {renderSearchFollowButton(appWithType)}
                </div>
              );
            })}

            <button
              type="button"
              className="search-explore-apps-pill"
              onClick={() => {
                refreshTopApps();
                navigate('/pi-apps');
              }}
            >
              Explore More Apps
            </button>
          </>
        )}
      </div>
    );
  };

  const renderDiscoveryResults = () => {
    if (discoveryLoading) {
      return <p className="no-results">Loading...</p>;
    }

    if (discoveryResults.length === 0) {
      return <p className="no-results">{getDiscoveryEmptyText()}</p>;
    }

    return (
      <>
        {discoveryResults.map((item, index) => {
          const isStore = item.type === 'store';
          const isOfficial = item.type === 'official';
          const isPiApp = item.type === 'piApp';
          const isMerchant = item.type === 'merchant';

          const displayName = isStore
            ? item.businessName || item.name || item.businessUsername
            : item.name || item.username;

          const username = isStore
            ? item.businessUsername || item.slug || item.username
            : item.username;

          const avatar = isStore
            ? getStoreLogo(item)
            : item.profilePic || placeholderProfile;

          return (
            <div
              className={`search-entry ${isPiApp ? 'search-app-card' : ''}`}
              key={`${item.type}-${item._id || username || index}`}
              onClick={() => handleDiscoveryClick(item)}
            >
              <img
                src={avatar}
                alt={displayName || 'Result'}
                className={`search-avatar ${
                  isStore || isPiApp || isOfficial ? 'search-avatar-square' : ''
                }`}
              />

              <div className="search-info">
                <div className="search-title-line">
                  <span className="search-name-with-badge">
                    <strong
                      dangerouslySetInnerHTML={{
                        __html: highlightMatch(displayName, trimmedQuery),
                      }}
                    ></strong>

                    {(isPiApp || isOfficial) && (
                      <img
                        src={goldTickIcon}
                        alt={isOfficial ? 'Official verified' : 'App verified'}
                        className="search-gold-tick"
                        title={isOfficial ? 'Official account' : 'App account'}
                      />
                    )}
                  </span>

                  {isStore && (
                    <img
                      src={storeIcon}
                      alt="store"
                      className="search-type-icon"
                      title="Store"
                    />
                  )}

                  {isMerchant && (
                    <img
                      src={userIcon}
                      alt="merchant"
                      className="search-type-icon"
                      title="Merchant"
                    />
                  )}
                </div>

                {isPiApp ? (
                  <>
                    <div className="search-app-meta-line">
                      {item.category && (
                        <span className="search-app-category">
                          {item.category}
                        </span>
                      )}

                      <span className="search-app-followers">
                        {formatFollowers(item.followersCount)}
                      </span>
                    </div>

                    {(item.bio || item.domain) && (
                      <span className="search-app-snippet">
                        {item.bio || item.domain}
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    <span
                      className="search-username"
                      dangerouslySetInnerHTML={{
                        __html: '@' + highlightMatch(username, trimmedQuery),
                      }}
                    ></span>

                    {(isMerchant || isOfficial) && (
                      <span className="search-app-followers">
                        {formatFollowers(item.followersCount)}
                      </span>
                    )}
                  </>
                )}
              </div>

              {(isPiApp || isMerchant || isOfficial) &&
                renderSearchFollowButton(item)}
            </div>
          );
        })}
      </>
    );
  };


const renderForYouContent = () => {
    if (trimmedQuery === '') {
      if (recentUsers.length === 0 && topApps.length === 0 && !topAppsLoading) {
        return <p className="no-results">Type to search…</p>;
      }

      return (
        <>
          {renderRecentUsers()}
          {renderTopApps()}
        </>
      );
    }

    if (userResults.length === 0 && storeResults.length === 0) {
      return <p className="no-results">No results found.</p>;
    }

    return (
      <>
        {storeResults.map((store, index) => {
          const tag = store.businessUsername || store.username;

          return (
            <div
              className="search-entry"
              key={`store-${index}`}
              onClick={() => navigate(`/store/${tag}`)}
            >
              <img
                src={getStoreLogo(store)}
                alt="Store"
                className="search-avatar search-avatar-square"
              />

              <div className="search-info">
                <div className="search-title-line">
                  <strong
                    dangerouslySetInnerHTML={{
                      __html: highlightMatch(store.businessName, trimmedQuery),
                    }}
                  ></strong>

                  <img src={storeIcon} alt="store" className="search-type-icon" />
                </div>

                <span
                  className="search-username"
                  dangerouslySetInnerHTML={{
                    __html: '@' + highlightMatch(tag, trimmedQuery),
                  }}
                ></span>
              </div>
            </div>
          );
        })}

        {userResults.map((usr, index) => (
          <div
            className="search-entry"
            key={`user-${index}`}
            onClick={() => handleUserResultClick(usr)}
          >
            <img
              src={usr.profilePic || placeholderProfile}
              alt="User"
              className={`search-avatar ${
                isSpecialUser(usr) ? 'search-avatar-square' : ''
              }`}
            />

            <div className="search-info">
              <div className="search-title-line">
                <span className="search-name-with-badge">
                  <strong
                    dangerouslySetInnerHTML={{
                      __html: highlightMatch(usr.name || usr.username, trimmedQuery),
                    }}
                  ></strong>

                  {isSpecialUser(usr) && (
                    <img
                      src={goldTickIcon}
                      alt={isAdminUser(usr) ? 'Admin verified' : 'App verified'}
                      className="search-gold-tick"
                      title={getSpecialTitle(usr)}
                    />
                  )}
                </span>

                {!isSpecialUser(usr) && (
                  <img
                    src={userIcon}
                    alt="user"
                    className="search-type-icon"
                    title="User"
                  />
                )}
              </div>

              <span
                className="search-username"
                dangerouslySetInnerHTML={{
                  __html: '@' + highlightMatch(usr.username, trimmedQuery),
                }}
              ></span>
            </div>
          </div>
        ))}
      </>
    );
  };

  return (
    <div className="search-screen">
      <div className="search-header">
        <div className="search-top-bar">
          <img
            src={backIcon}
            alt="Back"
            className="search-icon search-back-icon"
            onClick={() => navigate(-1)}
          />

          <div className="search-input-shell">
            <input
              type="text"
              placeholder={getSearchPlaceholder()}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />

            {query ? (
              <button
                type="button"
                className="search-clear-btn"
                onClick={() => setQuery('')}
              >
                <img src={closeIcon} alt="Clear" className="search-clear-icon" />
              </button>
            ) : null}
          </div>
        </div>

        {renderTabs()}
      </div>



<div
  ref={scrollAreaRef}
  onScroll={handleSearchScroll}
  className={`search-scroll-area ${
    activeTab === 'news'
      ? 'search-scroll-area--news'
      : ''
  }`}
>


  {activeTab === 'forYou' && renderForYouContent()}

{activeTab === 'news' && (
  <NewsTab query={query} />
)}

  {activeTab !== 'forYou' &&
    activeTab !== 'news' &&
    renderDiscoveryResults()}


</div>

      <BottomNav
        user={user}
        unreadDmCount={unreadDmCount}
        hidden={bottomNavHidden}
      />
    </div>
  );
}

export default Search;
